import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Modal } from "bootstrap";
import "./precos.css";

export const MODAL_PLANOS_ID = "modalPlanos";

/**
 * Abre o modal de compra de créditos.
 * Use SEMPRE esta função (em vez de data-bs-toggle ou window.bootstrap) para
 * garantir que só exista uma instância do Modal controlando o elemento.
 */
export function abrirModalPlanos() {
  const el = document.getElementById(MODAL_PLANOS_ID);
  if (!el) return;
  // Fecha teclado virtual / foco em inputs no mobile antes de abrir
  if (document.activeElement && typeof document.activeElement.blur === "function") {
    document.activeElement.blur();
  }
  Modal.getOrCreateInstance(el).show();
}

function Precos({user, creditos = 0, setCreditos}) {

  // Marca o <body> enquanto o modal está aberto, para elevar o backdrop
  // acima dos painéis mobile (que usam z-index alto). Ver precos.css.
  useEffect(() => {
    const el = document.getElementById(MODAL_PLANOS_ID);
    if (!el) return;
    const onShow = () => document.body.classList.add("modal-planos-aberto");
    const onHidden = () => document.body.classList.remove("modal-planos-aberto");
    el.addEventListener("show.bs.modal", onShow);
    el.addEventListener("hidden.bs.modal", onHidden);
    return () => {
      el.removeEventListener("show.bs.modal", onShow);
      el.removeEventListener("hidden.bs.modal", onHidden);
      // Se o componente desmontar com o modal aberto, limpa o estado do Bootstrap
      const estavaAberto = el.classList.contains("show");
      const inst = Modal.getInstance(el);
      if (inst) inst.dispose();
      document.body.classList.remove("modal-planos-aberto");
      if (estavaAberto) {
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("padding-right");
        document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
      }
    };
  }, []);

  const [loadingPlano, setLoadingPlano] = useState(null);
  const [loadingCartao, setLoadingCartao] = useState(false);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cardData, setCardData] = useState({
    numero: '',
    nome: '',
    validade: '',
    cvv: '',
    cpf: '',
    parcelas: '1'
  });
  const [cardErrors, setCardErrors] = useState({});

  const planos = [
    {
      id: "basico",
      nome: "Básico",
      emoji: "https://fonts.gstatic.com/s/e/notoemoji/latest/1f680/512.gif",
      creditos: 30,
      videos: 6,
      valor: 29.90,
      cor: "primary",
      gradiente: "#3f045c",
      beneficios: [
        "30 créditos",
        "Até 6 vídeos",
        "Locução automática",
        "Suporte por e-mail"
      ]
    },
    {
      id: "medio",
      nome: "Mais Vendido",
      emoji: "https://fonts.gstatic.com/s/e/notoemoji/latest/1f525/512.gif",
      creditos: 90,
      videos: 18,
      valor: 79.90,
      cor: "warning",
      destaque: true,
      gradiente: "#3f045c",
      beneficios: [
        "90 créditos",
        "Até 18 vídeos",
        "Locução automática",
        "Suporte prioritário",
        "Economia de 11%"
      ]
    },
    {
      id: "premium",
      nome: "Avançado",
      emoji: "https://fonts.gstatic.com/s/e/notoemoji/latest/1f3c6/512.gif",
      creditos: 150,
      videos: 30,
      valor: 119.90,
      cor: "success",
      gradiente: "#3f045c",
      beneficios: [
        "150 créditos",
        "Até 30 vídeos",
        "Locução automática",
        "Suporte VIP",
        "Economia de 20%"
      ]
    }
  ];

  async function pagarPlano(plano, valor, creditos) {
    try {
      setLoadingPlano(plano);

      const response = await fetch("https://videooferta.com/api/pagamento.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_usuario: user.id,
          email: user.email,
          nome: user.nome,
          plano,
          valor,
          creditos,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.erro) {
        alert(data.erro || "Erro ao gerar pagamento");
        return;
      }

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      alert("Erro ao conectar com o servidor");
    } finally {
      setLoadingPlano(null);
    }
  }

  function abrirModalCartao(plano) {
    setSelectedPlan(plano);
    setCardData({ numero: '', nome: '', validade: '', cvv: '', cpf: '', parcelas: '1' });
    setCardErrors({});
    setShowCardModal(true);
  }

  function fecharModalCartao() {
    setShowCardModal(false);
    setSelectedPlan(null);
    setCardData({ numero: '', nome: '', validade: '', cvv: '', cpf: '', parcelas: '1' });
    setCardErrors({});
  }

  function formatarCPF(valor) {
    const num = valor.replace(/\D/g, '').slice(0, 11);
    return num
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  function validarCPF(cpfStr) {
    const cpf = (cpfStr || '').replace(/\D/g, '');
    if (cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false;
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i), 10) * (10 - i);
    let dv = (soma * 10) % 11;
    if (dv === 10) dv = 0;
    if (dv !== parseInt(cpf.charAt(9), 10)) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i), 10) * (11 - i);
    dv = (soma * 10) % 11;
    if (dv === 10) dv = 0;
    return dv === parseInt(cpf.charAt(10), 10);
  }

  function formatarNumeroCartao(valor) {
    const numeros = valor.replace(/\D/g, '').slice(0, 16);
    return numeros.replace(/(\d{4})(?=\d)/g, '$1 ');
  }

  function formatarValidade(valor) {
    const numeros = valor.replace(/\D/g, '').slice(0, 4);
    if (numeros.length >= 2) {
      let mes = parseInt(numeros.slice(0, 2), 10);
      if (mes > 12) mes = 12;
      if (mes < 1 && numeros.slice(0, 2) !== '0' && numeros.slice(0, 2) !== '00') mes = 1;
      const mesStr = String(mes).padStart(2, '0');
      return mesStr + '/' + numeros.slice(2);
    }
    return numeros;
  }

  function handleCardInput(field, value) {
    let formattedValue = value;
    
    if (field === 'numero') {
      formattedValue = formatarNumeroCartao(value);
    } else if (field === 'validade') {
      formattedValue = formatarValidade(value);
    } else if (field === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    } else if (field === 'cpf') {
      formattedValue = formatarCPF(value);
    }
    
    setCardData(prev => ({ ...prev, [field]: formattedValue }));
    setCardErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validarCartao() {
    const errors = {};
    
    if (!cardData.numero || cardData.numero.replace(/\s/g, '').length < 16) {
      errors.numero = 'Número do cartão inválido';
    }
    if (!cardData.nome || cardData.nome.trim().length < 3) {
      errors.nome = 'Nome do titular é obrigatório';
    }
    if (!cardData.validade || cardData.validade.length < 5) {
      errors.validade = 'Data de validade inválida';
    } else {
      const [mes, ano] = cardData.validade.split('/');
      const mesNum = parseInt(mes, 10);
      if (mesNum < 1 || mesNum > 12) {
        errors.validade = 'Mês inválido (01-12)';
      }
    }
    if (!cardData.cvv || cardData.cvv.length < 3) {
      errors.cvv = 'CVV inválido';
    }
    if (!validarCPF(cardData.cpf)) {
      errors.cpf = 'CPF inválido';
    }
    
    setCardErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function processarPagamentoCartao() {
    if (!validarCartao()) return;
    
    try {
      setLoadingCartao(true);

      const response = await fetch("https://videooferta.com/api/pagamento-cartao.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_usuario: user.id,
          email: user.email,
          nome: user.nome,
          plano: selectedPlan.id,
          valor: selectedPlan.valor,
          creditos: selectedPlan.creditos,
          cpf: cardData.cpf.replace(/\D/g, ''),
          cartao: {
            numero: cardData.numero.replace(/\s/g, ''),
            nome: cardData.nome,
            validade: cardData.validade,
            cvv: cardData.cvv,
            cpf: cardData.cpf.replace(/\D/g, ''),
            parcelas: parseInt(cardData.parcelas)
          }
        }),
      });

      const data = await response.json();

      if (!response.ok || data.erro) {
        alert(data.erro || "Erro ao processar pagamento");
        return;
      }

      if (data.sucesso) {
        alert("Pagamento aprovado! Seus créditos foram adicionados.");
        fecharModalCartao();
        // Atualiza saldo sem reload
        if (user?.id) {
          fetch(`https://videooferta.com/api/saldo.php?id_user=${user.id}`)
            .then(res => res.json())
            .then(saldo => {
              if (!saldo.erro && setCreditos) {
                setCreditos(saldo.saldo);
              }
            })
            .catch(() => {});
        }
      }
    } catch (err) {
      alert("Erro ao conectar com o servidor");
    } finally {
      setLoadingCartao(false);
    }
  }

  function calcularParcelas() {
    if (!selectedPlan) return [];
    const parcelas = [];
    const maxParcelas = selectedPlan.valor >= 100 ? 12 : selectedPlan.valor >= 50 ? 6 : 3;
    
    for (let i = 1; i <= maxParcelas; i++) {
      const valorParcela = selectedPlan.valor / i;
      parcelas.push({
        quantidade: i,
        valor: valorParcela,
        label: i === 1 ? `1x de R$ ${valorParcela.toFixed(2).replace('.', ',')} (à vista)` : `${i}x de R$ ${valorParcela.toFixed(2).replace('.', ',')} sem juros`
      });
    }
    return parcelas;
  }

  // Renderizado direto no <body> via portal: no mobile o player (.col3) fica
  // display:none quando um menu está aberto, e o modal dentro dele sumia,
  // sobrando só o backdrop. No body ele também escapa de stacking contexts.
  return createPortal(
    <div className="modal fade" id={MODAL_PLANOS_ID} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '950px', width: '95%' }}>
        <div className="modal-content pricing-modal">

          {/* Header com gradiente */}
          <div className="pricing-header">
            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
            <div className="pricing-header-content">
              <img src="https://fonts.gstatic.com/s/e/notoemoji/latest/1fa99/512.gif" alt="Moeda" className="pricing-emoji" />
              <h4 className="pricing-title">Pacotes de Créditos</h4>
              <p className="pricing-subtitle">
                {creditos > 0 ? (
                  <><i className="fa-solid fa-coins me-1"></i>Seu saldo: <strong>{creditos} créditos</strong></>
                ) : (
                  'Escolha o plano ideal para você'
                )}
              </p>
            </div>
          </div>

          <div className="modal-body pricing-body">
            <div className="row g-4 justify-content-center">

              {planos.map((plano) => (
                <div className="col-md-4" key={plano.id}>
                  <div 
                    className={`pricing-card ${plano.destaque ? 'pricing-card-featured' : ''} ${hoveredPlan === plano.id ? 'pricing-card-hover' : ''}`}
                    onMouseEnter={() => setHoveredPlan(plano.id)}
                    onMouseLeave={() => setHoveredPlan(null)}
                  >
                    {/* Badge de destaque */}
                    {plano.destaque && (
                      <div className="pricing-badge">
                        <i className="fa-solid fa-fire me-1"></i>
                        MELHOR ESCOLHA
                      </div>
                    )}

                    {/* Header do card */}
                    <div className="pricing-card-header" style={{ background: plano.gradiente }}>
                      <img src={plano.emoji} alt={plano.nome} className="pricing-plan-emoji" />
                      <h5 className="pricing-plan-name">{plano.nome}</h5>
                    </div>

                    {/* Corpo do card */}
                    <div className="pricing-card-body">
                      {/* Créditos em destaque */}
                      <div className="pricing-credits">
                        <span className="pricing-credits-label">Créditos</span>
                        <span className="pricing-credits-value">{plano.creditos}</span>
                      </div>

                      {/* Descrição */}
                      <p className="pricing-description">
                        Ideal para quem quer criar {plano.videos > 10 ? 'muitos' : 'poucos'} vídeos. 
                        Com este plano, você consegue baixar aproximadamente:
                      </p>

                      {/* Vídeos */}
                      <div className="pricing-videos">
                        <i className="fa-solid fa-circle-check text-success me-2"></i>
                        <span>~{plano.videos} vídeos com locução automática</span>
                      </div>

                      {/* Lista de benefícios */}
                      <ul className="pricing-benefits">
                        {plano.beneficios.map((beneficio, idx) => (
                          <li key={idx}>
                            <i className="fa-solid fa-check"></i>
                            <span>{beneficio}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Preço */}
                      <div className="pricing-price">
                        <span className="pricing-currency">R$</span>
                        <span className="pricing-amount">{plano.valor.toFixed(2).replace('.', ',')}</span>
                      </div>

                      {/* Botões de Pagamento */}
                      <div className="pricing-buttons">
                        <button
                          className={`pricing-button pricing-button-pix ${plano.destaque ? 'pricing-button-featured' : ''}`}
                          disabled={loadingPlano !== null}
                          onClick={() => pagarPlano(plano.id, plano.valor, plano.creditos)}
                        >
                          {loadingPlano === plano.id ? (
                            <>
                              <i className="fa-solid fa-spinner fa-spin me-2"></i>
                              Gerando Pix...
                            </>
                          ) : (
                            <>
                              <i className="fa-brands fa-pix me-2"></i>
                              Pix
                            </>
                          )}
                        </button>
                        <button
                          className="pricing-button pricing-button-card"
                          disabled={loadingPlano !== null}
                          onClick={() => abrirModalCartao(plano)}
                        >
                          <i className="fa-solid fa-credit-card me-2"></i>
                          Cartão
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

            </div>

            {/* Rodapé com informações */}
            <div className="pricing-footer">
              <div className="pricing-footer-item">
                <i className="fa-solid fa-shield-halved"></i>
                <span>Pagamento 100% seguro</span>
              </div>
              <div className="pricing-footer-item">
                <i className="fa-solid fa-bolt"></i>
                <span>Créditos liberados na hora</span>
              </div>
              <div className="pricing-footer-item">
                <i className="fa-solid fa-calendar-check"></i>
                <span>Válidos por 30 dias</span>
              </div>
            </div>

            <p className="pricing-note">
              * Cada download de vídeo custa 5 créditos
            </p>
          </div>

        </div>
      </div>

      {/* Modal de Pagamento com Cartão */}
      {showCardModal && selectedPlan && (
        <div className="card-modal-overlay" onClick={fecharModalCartao}>
          <div className="card-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header do Modal */}
            <div className="card-modal-header">
              <button className="card-modal-close" onClick={fecharModalCartao}>
                <i className="fa-solid fa-times"></i>
              </button>
              <div className="card-modal-header-content">
                <i className="fa-solid fa-credit-card card-modal-icon"></i>
                <h4 className="card-modal-title">Pagamento com Cartão</h4>
                <p className="card-modal-subtitle">Plano {selectedPlan.nome} • R$ {selectedPlan.valor.toFixed(2).replace('.', ',')}</p>
              </div>
            </div>

            {/* Body do Modal */}
            <div className="card-modal-body">
              {/* Número do Cartão */}
              <div className="card-input-group">
                <label className="card-input-label">Número do Cartão</label>
                <div className={`card-input-wrapper ${cardErrors.numero ? 'card-input-error' : ''}`}>
                  <i className="fa-solid fa-credit-card card-input-icon"></i>
                  <input
                    type="text"
                    className="card-input"
                    placeholder="0000 0000 0000 0000"
                    value={cardData.numero}
                    onChange={(e) => handleCardInput('numero', e.target.value)}
                  />
                </div>
                {cardErrors.numero && <span className="card-error-text">{cardErrors.numero}</span>}
              </div>

              {/* Nome do Titular */}
              <div className="card-input-group">
                <label className="card-input-label">Nome do Titular</label>
                <div className={`card-input-wrapper ${cardErrors.nome ? 'card-input-error' : ''}`}>
                  <i className="fa-solid fa-user card-input-icon"></i>
                  <input
                    type="text"
                    className="card-input"
                    placeholder="Nome como está no cartão"
                    value={cardData.nome}
                    onChange={(e) => handleCardInput('nome', e.target.value.toUpperCase())}
                  />
                </div>
                {cardErrors.nome && <span className="card-error-text">{cardErrors.nome}</span>}
              </div>

              {/* Validade e CVV */}
              <div className="card-input-row">
                <div className="card-input-group">
                  <label className="card-input-label">Validade</label>
                  <div className={`card-input-wrapper ${cardErrors.validade ? 'card-input-error' : ''}`}>
                    <i className="fa-solid fa-calendar card-input-icon"></i>
                    <input
                      type="text"
                      className="card-input"
                      placeholder="MM/AA"
                      value={cardData.validade}
                      onChange={(e) => handleCardInput('validade', e.target.value)}
                    />
                  </div>
                  {cardErrors.validade && <span className="card-error-text">{cardErrors.validade}</span>}
                </div>

                <div className="card-input-group">
                  <label className="card-input-label">CVV</label>
                  <div className={`card-input-wrapper ${cardErrors.cvv ? 'card-input-error' : ''}`}>
                    <i className="fa-solid fa-lock card-input-icon"></i>
                    <input
                      type="text"
                      className="card-input"
                      placeholder="000"
                      value={cardData.cvv}
                      onChange={(e) => handleCardInput('cvv', e.target.value)}
                    />
                  </div>
                  {cardErrors.cvv && <span className="card-error-text">{cardErrors.cvv}</span>}
                </div>
              </div>

              {/* CPF do Titular */}
              <div className="card-input-group">
                <label className="card-input-label">CPF do Titular</label>
                <div className={`card-input-wrapper ${cardErrors.cpf ? 'card-input-error' : ''}`}>
                  <i className="fa-solid fa-id-card card-input-icon"></i>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="card-input"
                    placeholder="000.000.000-00"
                    value={cardData.cpf}
                    onChange={(e) => handleCardInput('cpf', e.target.value)}
                  />
                </div>
                {cardErrors.cpf && <span className="card-error-text">{cardErrors.cpf}</span>}
              </div>

              {/* Parcelas */}
              <div className="card-input-group">
                <label className="card-input-label">Parcelas</label>
                <div className="card-input-wrapper card-select-wrapper">
                  <i className="fa-solid fa-layer-group card-input-icon"></i>
                  <select
                    className="card-input card-select"
                    value={cardData.parcelas}
                    onChange={(e) => handleCardInput('parcelas', e.target.value)}
                  >
                    {calcularParcelas().map((p) => (
                      <option key={p.quantidade} value={p.quantidade}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <i className="fa-solid fa-chevron-down card-select-arrow"></i>
                </div>
              </div>

              {/* Bandeiras aceitas */}
              <div className="card-brands">
                <span className="card-brands-label">Aceitamos:</span>
                <div className="card-brands-icons">
                  <i className="fa-brands fa-cc-visa"></i>
                  <i className="fa-brands fa-cc-mastercard"></i>
                  <i className="fa-brands fa-cc-amex"></i>
                  <i className="fa-brands fa-cc-diners-club"></i>
                </div>
              </div>

              {/* Botão de Pagar */}
              <button 
                className="card-pay-button"
                onClick={processarPagamentoCartao}
                disabled={loadingCartao}
              >
                {loadingCartao ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin me-2"></i>
                    Processando...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-lock me-2"></i>
                    Pagar R$ {selectedPlan.valor.toFixed(2).replace('.', ',')}
                  </>
                )}
              </button>

              {/* Segurança */}
              <div className="card-security">
                <i className="fa-solid fa-shield-halved"></i>
                <span>Pagamento 100% seguro e criptografado</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

export default Precos;
